// ─── App-specific Zod schemas ───────────────────────────────────────────────
// This file is NEVER overwritten by boilerplate updates.
// Add your app-specific request validation schemas here.

import { z } from 'zod';

// ─── Mobile: Social Login ───────────────────────────────────────────────────
export const socialLoginSchema = z.object({
  provider: z.enum(['google', 'apple', 'facebook']),
  idToken: z.string().min(1),
});
export type SocialLoginInput = z.infer<typeof socialLoginSchema>;

// ─── Mobile: Phone Login ────────────────────────────────────────────────────
export const phoneLoginSchema = z.object({
  phone: z.string().regex(/^\+[1-9]\d{1,14}$/),
  code: z.string().length(6).optional(),
});
export type PhoneLoginInput = z.infer<typeof phoneLoginSchema>;

// ─── Mobile: Magic Link ─────────────────────────────────────────────────────
export const magicLinkRequestSchema = z.object({
  email: z.string().email(),
});
export type MagicLinkRequestInput = z.infer<typeof magicLinkRequestSchema>;

export const magicLinkConfirmSchema = z.object({
  token: z.string().min(32),
});
export type MagicLinkConfirmInput = z.infer<typeof magicLinkConfirmSchema>;

// ─── Web: Custom Registration ───────────────────────────────────────────────
// Example: extend the base registration with custom fields
export const customRegisterSchema = z.object({
  name: z.string().min(2).max(100),
  email: z.string().email(),
  password: z.string().min(8).max(128),
  phone: z.string().regex(/^[0-9+()\-\s]{7,20}$/).optional(),
  company: z.string().max(200).optional(),
  acceptTerms: z.boolean().refine((v) => v === true, {
    message: 'You must accept the terms and conditions',
  }),
});
export type CustomRegisterInput = z.infer<typeof customRegisterSchema>;

// ─── Web: Guest Checkout (ePayco) ──────────────────────────────────────────
export const guestCheckoutSchema = z.object({
  planId: z.string().uuid(),
  guestName: z.string().min(2).max(100),
  guestEmail: z.string().email().optional(),
  guestPhone: z.string().regex(/^\+?[0-9\s\-]{7,20}$/).optional(),
  guestDocType: z.enum(['CC', 'CE', 'NIT', 'PPN', 'TI', 'DNI']).optional(),
  guestDocNumber: z.string().max(20).optional(),
  guestAddress: z.string().max(300).optional(),
  discountCode: z.string().max(60).optional(),
});
export type GuestCheckoutInput = z.infer<typeof guestCheckoutSchema>;

// ─── Web: Guest Checkout Config Response ───────────────────────────────────
export interface GuestCheckoutConfig {
  guestEnabled: boolean;
  requireEmail: boolean;
  requirePhone: boolean;
}

// ─── Web: Stripe Checkout (logged-in user) ─────────────────────────────────
export const stripeCheckoutSchema = z.object({
  planId: z.string().uuid(),
  discountCode: z.string().max(60).optional(),
  successUrl: z.string().url().optional(),
  cancelUrl: z.string().url().optional(),
});
export type StripeCheckoutInput = z.infer<typeof stripeCheckoutSchema>;

// ─── Web: Stripe Guest Checkout ────────────────────────────────────────────
export const stripeGuestCheckoutSchema = z.object({
  planId: z.string().uuid(),
  guestName: z.string().min(2).max(100),
  guestEmail: z.string().email(),
  guestPhone: z.string().regex(/^\+?[0-9\s\-]{7,20}$/).optional(),
  guestAddress: z.string().max(300).optional(),
  discountCode: z.string().max(60).optional(),
  successUrl: z.string().url().optional(),
  cancelUrl: z.string().url().optional(),
});
export type StripeGuestCheckoutInput = z.infer<typeof stripeGuestCheckoutSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// ICFES — Platform admin (question bank management)
// ═══════════════════════════════════════════════════════════════════════════

// ─── ICFES: Create PDF Source ──────────────────────────────────────────────
export const createPdfSourceSchema = z.object({
  pdf_name: z.string().min(1).max(255),
});
export type CreatePdfSourceInput = z.infer<typeof createPdfSourceSchema>;

// ─── ICFES: Import JSON ───────────────────────────────────────────────────
export const importJsonSchema = z.object({
  json_content: z.string().min(1),
});
export type ImportJsonInput = z.infer<typeof importJsonSchema>;

// ─── ICFES: Create Catalog Entry ──────────────────────────────────────────
export const createCatalogSchema = z.object({
  type: z.enum(['subject', 'component', 'competency', 'difficulty']),
  name: z.string().min(1).max(100),
  id_subject: z.string().uuid().optional(),
});
export type CreateCatalogInput = z.infer<typeof createCatalogSchema>;

// ─── ICFES: Update Question ───────────────────────────────────────────────
export const updateQuestionSchema = z.object({
  question_text: z.string().optional(),
  id_enunciado: z.string().uuid().optional().nullable(),
  enunciado_text: z.string().nullish(),
  id_subject: z.string().uuid().optional().nullable(),
  id_component: z.string().uuid().optional().nullable(),
  id_competency: z.string().uuid().optional().nullable(),
  id_difficulty: z.string().uuid().optional().nullable(),
  id_correct_option_final: z.string().uuid().optional().nullable(),
  explain_correct_final: z.string().optional().nullable(),
  answer_multiple_choice: z.boolean().optional(),
  from_grade: z.coerce.number().int().min(1).max(13).optional().nullable(),
  enunciado_with_image: z.boolean().optional(),
  pregunta_with_image: z.boolean().optional(),
  dificulty_explain: z.string().optional().nullable(),
  pdf_name: z.string().max(255).optional().nullable(),
  options: z
    .array(
      z.object({
        id: z.string().uuid().optional(),
        label: z.string().length(1),
        type: z.enum(['text', 'image']),
        content: z.string(),
      }),
    )
    .optional(),
});
export type UpdateQuestionInput = z.infer<typeof updateQuestionSchema>;

// ─── ICFES: Update Question Status ────────────────────────────────────────
export const updateQuestionStatusSchema = z.object({
  status: z.enum(['draft', 'active', 'disabled']),
});
export type UpdateQuestionStatusInput = z.infer<typeof updateQuestionStatusSchema>;

// ─── ICFES: Update AI Explanation ─────────────────────────────────────────
export const updateAiExplanationSchema = z.object({
  id_correct_option: z.string().uuid().optional().nullable(),
  correct_option_label: z.string().length(1).optional().nullable(),
  explain_correct: z.string().optional().nullable(),
  explain_no_others: z
    .union([
      z.array(z.object({ label: z.string(), explanation: z.string() })),
      z.record(z.string()),
    ])
    .optional()
    .nullable(),
});
export type UpdateAiExplanationInput = z.infer<typeof updateAiExplanationSchema>;

// ─── ICFES: Create Enunciado ─────────────────────────────────────────────
export const createEnunciadoSchema = z.object({
  statement_text: z.string().min(1).max(10000),
  id_pdf_source: z.string().uuid().optional().nullable(),
  part_index: z.number().int().optional().nullable(),
  slice_start_page: z.number().int().optional().nullable(),
  slice_end_page: z.number().int().optional().nullable(),
});
export type CreateEnunciadoInput = z.infer<typeof createEnunciadoSchema>;

// ─── ICFES: Update Enunciado ─────────────────────────────────────────────
export const updateEnunciadoSchema = z.object({
  statement_text: z.string().min(1).max(10000).optional(),
  id_pdf_source: z.string().uuid().optional().nullable(),
  part_index: z.number().int().optional().nullable(),
  slice_start_page: z.number().int().optional().nullable(),
  slice_end_page: z.number().int().optional().nullable(),
});
export type UpdateEnunciadoInput = z.infer<typeof updateEnunciadoSchema>;

// ─── ICFES: JSON Question Item (for import validation) ─────────────────────
export const jsonQuestionItemSchema = z.object({
  question_id: z.string().min(1, { message: 'question_id is required' }),
  pregunta: z.string().min(1, { message: 'pregunta is required and cannot be empty' }),
  answer_multiple_choice: z.coerce.boolean().default(false),
  linked_to_enunciado: z.boolean().optional(),
  enunciado: z.string().nullish(),
  options: z
    .array(
      z.object({
        label: z.string().min(1),
        type: z.enum(['text', 'image']),
        content: z.string(),
        is_correct: z.boolean().optional(),
      }),
    )
    .min(1, { message: 'At least one option is required' }),
  subject: z.string().optional(),
  component: z.string().optional(),
  competency: z.string().optional(),
  dificulty: z.string().optional(),
  correct_answer_ai_option: z.string().nullish(),
  explain_correct_ai: z.string().nullish(),
  explain_no_others_ai: z
    .union([
      z.array(z.object({ label: z.string(), explanation: z.string() })),
      z.record(z.string()),
    ])
    .nullish(),
  page_number: z.number().optional(),
  batch_number: z.number().optional(),
  part_index: z.number().optional(),
  slice_start_page: z.number().optional(),
  slice_end_page: z.number().optional(),
  enunciado_with_image: z.coerce.boolean().optional(),
  pregunta_with_image: z.coerce.boolean().optional(),
  from_grade: z.coerce.number().int().min(1).max(13).optional(),
  dificulty_explain: z.string().nullish(),
  pdf_name: z.string().max(255).nullish(),
});
export type JsonQuestionItemInput = z.infer<typeof jsonQuestionItemSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// SaberLab — Mobile app (student domain)
// ═══════════════════════════════════════════════════════════════════════════

// ─── Mobile: Register ──────────────────────────────────────────────────────
export const mobileRegisterSchema = z.object({
  name: z.string().min(2).max(100),
  email: z.string().email(),
  password: z.string().min(8).max(128),
  docType: z.string().min(1).max(40),
  docNumber: z.string().min(1).max(50),
  grade: z.coerce.number().int().min(1).max(11), // obligatorio: grado escolar colombiano 1-11
  universityType: z.enum(['public', 'private', 'none']).default('none'),
  idUniversity: z.string().uuid().optional().nullable(),
  idCareer: z.string().uuid().optional().nullable(),
  idUniversityCareer: z.string().uuid().optional().nullable(),
});
export type MobileRegisterInput = z.infer<typeof mobileRegisterSchema>;

// ─── Mobile: Profile setup (precarga) ──────────────────────────────────────
export const profileSetupSchema = z.object({
  idAvatar: z.string().uuid(),
  nickname: z
    .string()
    .regex(/^[A-Za-z0-9]{3,20}$/, 'Nickname must be 3-20 letters/numbers'),
  gender: z.enum(['male', 'female']).optional(),
  idDepartment: z.string().uuid(),
  idMunicipality: z.string().uuid(),
  idSchool: z.string().uuid().optional().nullable(),
  intensity: z.enum(['5', '10', '15', '20']),
});
export type ProfileSetupInput = z.infer<typeof profileSetupSchema>;

// ─── Mobile: Training ──────────────────────────────────────────────────────
export const trainingStartSchema = z.object({
  methodology: z.enum(['questions', 'time']),
  targetValue: z.number().int().min(1).max(60),
  focus: z.enum(['specific', 'all', 'elimination']).default('all'),
  difficulty: z.enum(['low', 'medium', 'high', 'random']).default('random'),
  idSubject: z.string().uuid().optional().nullable(),
});
export type TrainingStartInput = z.infer<typeof trainingStartSchema>;

// ─── Mobile: Generic answer submission ─────────────────────────────────────
export const answerSubmitSchema = z.object({
  mode: z.enum(['daily', 'training', 'simulacro', 'race', 'trap', 'duel']),
  idQuestion: z.string().uuid(),
  idSession: z.string().uuid().optional().nullable(),
  selectedLabel: z.string().length(1),
  idSelectedOption: z.string().uuid().optional().nullable(),
  timeSpentMs: z.number().int().min(0).default(0),
});
export type AnswerSubmitInput = z.infer<typeof answerSubmitSchema>;

// ─── Mobile: Simulacro ─────────────────────────────────────────────────────
export const simulacroStartSchema = z.object({
  mode: z.enum(['full', 'subject']).default('full'),
  subjects: z.array(z.string().uuid()).optional(),
});
export type SimulacroStartInput = z.infer<typeof simulacroStartSchema>;

export const simulacroSubjectSubmitSchema = z.object({
  session: z.enum(['1', '2']),
  idSubject: z.string().uuid(),
  answers: z.array(
    z.object({
      idQuestion: z.string().uuid(),
      selectedLabel: z.string().length(1),
      idSelectedOption: z.string().uuid().optional().nullable(),
      timeSpentMs: z.number().int().min(0).default(0),
    }),
  ),
});
export type SimulacroSubjectSubmitInput = z.infer<typeof simulacroSubjectSubmitSchema>;

export const simulacroFinishSchema = z.object({
  session: z.enum(['1', '2']),
});
export type SimulacroFinishInput = z.infer<typeof simulacroFinishSchema>;

export const simulacroSessionStartSchema = z.object({
  session: z.enum(['1', '2']),
});
export type SimulacroSessionStartInput = z.infer<typeof simulacroSessionStartSchema>;

export const simulacroPauseSchema = z.object({
  session: z.enum(['1', '2']),
  scope: z.enum(['question', 'subject']).default('question'),
});
export type SimulacroPauseInput = z.infer<typeof simulacroPauseSchema>;

export const simulacroResumeSchema = z.object({
  session: z.enum(['1', '2']),
});
export type SimulacroResumeInput = z.infer<typeof simulacroResumeSchema>;

// ─── Mobile: Duelo de Mentes ───────────────────────────────────────────────
export const duelCreateSchema = z.object({
  mode: z.enum(['random', 'subject']).default('random'),
  idSubject: z.string().uuid().optional().nullable(),
});
export type DuelCreateInput = z.infer<typeof duelCreateSchema>;

export const duelJoinSchema = z.object({
  inviteCode: z.string().min(4).max(24),
});
export type DuelJoinInput = z.infer<typeof duelJoinSchema>;

export const duelAnswerSchema = z.object({
  idQuestion: z.string().uuid(),
  idSelectedOption: z.string().uuid().optional().nullable(),
  selectedLabel: z.string().length(1),
  timeSpentMs: z.number().int().min(0).default(0),
});
export type DuelAnswerInput = z.infer<typeof duelAnswerSchema>;

// ─── Mobile: AI Chat ───────────────────────────────────────────────────────
export const aiChatSchema = z.object({
  idQuestion: z.string().uuid().optional().nullable(),
  mode: z.string().max(20).default('general'),
  message: z.string().min(1).max(2000),
  history: z
    .array(z.object({ role: z.enum(['user', 'assistant']), content: z.string() }))
    .max(20)
    .optional(),
});
export type AiChatInput = z.infer<typeof aiChatSchema>;

// ─── Mobile: Minigames results ─────────────────────────────────────────────
export const raceFinishSchema = z.object({
  durationSeconds: z.number().int().min(0),
  questionsAnswered: z.number().int().min(0),
  correctCount: z.number().int().min(0),
});
export type RaceFinishInput = z.infer<typeof raceFinishSchema>;

export const trapFinishSchema = z.object({
  questionsAnswered: z.number().int().min(0),
  correctCount: z.number().int().min(0),
  totalTimeMs: z.number().int().min(0),
});
export type TrapFinishInput = z.infer<typeof trapFinishSchema>;

export const trapEliminateSchema = z.object({
  idQuestion: z.string().uuid(),
  selectedLabel: z.string().min(1).max(1),
  eliminatedLabels: z.array(z.string().min(1).max(1)).default([]),
});
export type TrapEliminateInput = z.infer<typeof trapEliminateSchema>;

// ─── Mobile: Friends ───────────────────────────────────────────────────────
export const friendRequestSchema = z.object({
  nickname: z.string().regex(/^[A-Za-z0-9]{3,20}$/),
});
export type FriendRequestInput = z.infer<typeof friendRequestSchema>;

export const friendActionSchema = z.object({
  idFriendship: z.string().uuid(),
});
export type FriendActionInput = z.infer<typeof friendActionSchema>;

// ─── Mobile: Profile settings ──────────────────────────────────────────────
export const nicknameUpdateSchema = z.object({
  nickname: z.string().regex(/^[A-Za-z0-9]{3,20}$/),
});
export type NicknameUpdateInput = z.infer<typeof nicknameUpdateSchema>;

export const passwordChangeSchema = z.object({
  currentPassword: z.string().min(1).max(128),
  newPassword: z.string().min(8).max(128),
});
export type PasswordChangeInput = z.infer<typeof passwordChangeSchema>;
